import type {
	PublicPluginAPI,
	OptionalPlugin,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

import type {
	OnInlineRemixActionedCallback,
	PassThroughCommandProps,
} from '../command-handlers/command-handler';
import type { AbortOptions, AbortReason } from '../types/abort-reason';
import { toStreamAbortReason } from '../types/abort-reason';

type ChatRemixEdit = { instrumentationId: string; settled: boolean };

export class DocumentImageUpdateCommandProcessor {
	private onUploadAndInsertMedia?: PassThroughCommandProps['onUploadAndInsertMedia'];
	private editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>;
	private hasPrepared = false;
	private streamRevision = 0;
	private chatRemixEdit?: ChatRemixEdit;
	private lastTraceId?: string;
	private onInlineRemixActioned?: OnInlineRemixActionedCallback;
	private remixSubtype?: string;
	/** Attempt token captured at stream start. */
	private inlineRovoEditAttemptId?: string;
	/** Last bridge target forwarded to the CE claim boundary, including an explicit missing target. */
	private lastInlineRovoEditClaimTarget?: string;
	private hasForwardedInlineRovoEditClaim = false;

	constructor(
		onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'],
		editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>,
		onInlineRemixActioned?: OnInlineRemixActionedCallback,
	) {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
		this.editorAPI = editorAPI;
		this.onInlineRemixActioned = onInlineRemixActioned;
	}

	updateCallback(onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia']): void {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
	}

	streamChunk(
		data: ConfluenceUpdateImageOnPageData,
		isFinished: boolean,
		traceId?: string,
		singleInstrumentationId?: string,
		isInlineEdit?: boolean,
		chatRemixInstrumentationId?: string,
	): void {
		this.streamRevision += 1;
		const streamRevision = this.streamRevision;

		const chatRemixEdit = chatRemixInstrumentationId
			? this.chatRemixEdit?.instrumentationId === chatRemixInstrumentationId
				? this.chatRemixEdit
				: { instrumentationId: chatRemixInstrumentationId, settled: false }
			: undefined;
		this.chatRemixEdit = chatRemixEdit;
		this.lastTraceId = traceId ?? this.lastTraceId;

		// If the stream errored, clear the AI generating state and bail out
		if (
			'__isError' in data &&
			(data as ConfluenceUpdateImageOnPageData & { __isError?: boolean }).__isError
		) {
			this.claimInlineRovoEditAttempt(data);
			this.streamError('backend_action', traceId);
			return;
		}

		this.captureRemixMetadata(data);

		// A safety / AUP violation is streamed as a finished ACTION_COMPLETE with
		// `isSafetyViolation: true` and no media. This is an expected outcome, not
		// a system failure, so it must not publish a `taskFail` operational metric
		// (which is pure noise). Publish the outcome as a success and skip the
		// (impossible) media insertion.
		if (isFinished && data.isSafetyViolation) {
			this.claimInlineRovoEditAttempt(data);
			this.reportInlineRovoEditAttempt(this.inlineRovoEditAttemptId, 'handoff');
			const remixSubtype = this.remixSubtype;
			this.hasPrepared = false;
			this.lastTraceId = undefined;
			this.remixSubtype = undefined;
			this.reportExperience(chatRemixEdit, () =>
				remixRegenerateExperience.success({
					...(chatRemixInstrumentationId && {
						instrumentationId: chatRemixInstrumentationId,
						isSafetyViolation: true,
					}),
					remixType: 'infographic',
					...(remixSubtype && { remixSubtype }),
				}),
			);
			return;
		}
		// Variant-picker image updates are handled FE-side (useUpdateRemix ->
		// useOnComplete uploads + inserts and drives the loading state); the BE
		// push here would double both. Rovo-chat updates aren't tracked, so they
		// still flow through.
		if (
			data.previousMediaFileId &&
			this.editorAPI?.malleableUi?.actions.isVariantUpdateInFlight(data.previousMediaFileId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			if (isFinished) {
				this.lastTraceId = undefined;
				this.remixSubtype = undefined;
			}
			return;
		}

		this.claimInlineRovoEditAttempt(data);
		// ACTION_PREPARE marks a stream that must report an abort/error outcome.
		if (data.previousMediaFileId) {
			this.hasPrepared = true;
		}

		if (isFinished) {
			const attemptId = this.inlineRovoEditAttemptId;
			this.reportInlineRovoEditAttempt(attemptId, 'result-ready');
			this.reportInlineRovoEditAttempt(attemptId, 'direct-insertion-started');
			this.hasPrepared = false;
			if (!this.onUploadAndInsertMedia) {
				if (chatRemixInstrumentationId) {
					this.reportExperience(chatRemixEdit, () =>
						remixRegenerateExperience.fail({
							instrumentationId: chatRemixInstrumentationId,
							error: 'missing_image_upload_handler',
						}),
					);
				}
				this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
				return;
			}
			let insertionResult: 'inserted' | 'target-missing' | 'failed' | undefined;
			const uploadPromise =
				attemptId || chatRemixInstrumentationId
					? this.onUploadAndInsertMedia(data.mediaUrl, data, undefined, {
							...(attemptId && { requireInsertionAcknowledgement: true }),
							...(chatRemixInstrumentationId && {
								onInsertionResult: (result: 'inserted' | 'target-missing' | 'failed') => {
									insertionResult = result;
								},
							}),
						})
					: this.onUploadAndInsertMedia(data.mediaUrl, data);
			void uploadPromise
				.then((result) => {
					if (chatRemixInstrumentationId && insertionResult !== 'inserted') {
						if (insertionResult === 'target-missing') {
							this.reportExperience(chatRemixEdit, () =>
								remixRegenerateExperience.abort({
									instrumentationId: chatRemixInstrumentationId,
									reason: 'target_no_longer_available',
								}),
							);
						} else {
							this.reportExperience(chatRemixEdit, () =>
								remixRegenerateExperience.fail({
									instrumentationId: chatRemixInstrumentationId,
									error: 'upload_and_insert_failed',
								}),
							);
						}
					}
					if (attemptId && !result) {
						this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
						this.reportExperience(chatRemixEdit, () =>
							remixRegenerateExperience.fail({
								...(chatRemixInstrumentationId && {
									instrumentationId: chatRemixInstrumentationId,
								}),
								error: 'upload_and_insert_failed',
								remixType: 'infographic',
								...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
							}),
						);
						this.remixSubtype = undefined;
						return;
					}
					const didCompleteInlineEdit = this.reportInlineRovoEditAttempt(attemptId, 'inserted');

					const remixSubtype = this.remixSubtype;
					this.remixSubtype = undefined;

					if (
						data.imageEditAction === 'removeBackground' &&
						UNSAFE_expValNoExposure('cc_image_remove_background', 'isEnabled', false) &&
						result?.type !== 'update'
					) {
						this.reportExperience(chatRemixEdit, () =>
							remixRegenerateExperience.fail({
								...(chatRemixInstrumentationId && {
									instrumentationId: chatRemixInstrumentationId,
								}),
								error: 'upload_and_replace_failed',
								remixType: 'infographic',
								...(remixSubtype && { remixSubtype }),
							}),
						);
						return;
					}

					// Image presets omit the previous media ID; the picker completes Regenerate after rendering
					if (
						!fg('platform_editor_ai_remix_toolbar') ||
						data.previousMediaFileId ||
						isInlineEdit ||
						chatRemixInstrumentationId
					) {
						this.reportExperience(chatRemixEdit, () =>
							remixRegenerateExperience.success({
								...(chatRemixInstrumentationId && {
									instrumentationId: chatRemixInstrumentationId,
								}),
								remixType: 'infographic',
								...(remixSubtype && { remixSubtype }),
							}),
						);
					}
					this.lastTraceId = undefined;
					// `result.fieldId` is the upload-side upfront id assigned by
					// mediaClient and written onto mediaNode.attrs.id; the BE-side
					// `data.mediaFileId` is a different identifier and won't match
					// the live node when the toolbar looks up the stack entry.
					const liveFieldId = result?.fieldId;
					if (result?.type === 'update' && liveFieldId && isInlineEdit) {
						this.onInlineRemixActioned?.({
							actionKey: 'image-create',
							createdContentMetadata: {
								mediaFileId: liveFieldId,
								previousMediaFileId: data.previousMediaFileId,
							},
							createdContentType: 'image',
							dynamicUiSubtype: remixSubtype,
							identity: {
								attribute: 'id',
								value: liveFieldId,
							},
							singleInstrumentationId,
						});
					}
					if (data.previousMediaFileId && liveFieldId && fg('platform_editor_ai_remix_toolbar')) {
						// `dynamicUiSubtype` is intentionally omitted: the reducer inherits
						// it from the matched entry (the backend's echoed value is
						// unreliable for edits that aren't told the current subtype).
						const cmd = this.editorAPI?.malleableUi?.commands.addBackendRemixHistoryStackEntry({
							previousFieldId: data.previousMediaFileId,
							fieldId: liveFieldId,
						});
						if (cmd) {
							this.editorAPI?.core?.actions.execute(cmd);
						}
					}
					if (
						data.imageEditAction === 'removeBackground' &&
						result?.type === 'update' &&
						UNSAFE_expValNoExposure('cc_image_remove_background', 'isEnabled', false) &&
						streamRevision === this.streamRevision &&
						(!attemptId || didCompleteInlineEdit)
					) {
						const editorAPI = this.editorAPI as PassThroughCommandProps['editorAPI'] | undefined;
						editorAPI?.aiExperience?.actions.endInlineRovoStreaming?.();
						editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(false);

						const command = editorAPI?.malleableUi?.commands.stopRemixThinking({
							reopenModal: false,
						});
						if (command) {
							editorAPI?.core?.actions.execute(command);
						}
					}
				})
				.catch((error: unknown) => {
					this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
					const remixSubtype = this.remixSubtype;
					const failureTraceId = traceId ?? this.lastTraceId;
					this.lastTraceId = undefined;
					this.remixSubtype = undefined;
					this.reportExperience(chatRemixEdit, () =>
						remixRegenerateExperience.fail({
							...(chatRemixInstrumentationId && { instrumentationId: chatRemixInstrumentationId }),
							error: error instanceof Error ? error.message : 'upload_and_insert_failed',
							failureReason: 'upload_and_insert_failed',
							failureStage: 'frontend_apply',
							remixType: 'infographic',
							...(remixSubtype && { remixSubtype }),
							...(failureTraceId && { traceId: failureTraceId }),
						}),
					);
				});
		}
	}

	private reportExperience(chatRemixEdit: ChatRemixEdit | undefined, report: () => void): void {
		if (chatRemixEdit?.settled) {
			return;
		}
		if (chatRemixEdit) {
			chatRemixEdit.settled = true;
		}
		report();
	}

	private captureRemixMetadata(data: ConfluenceUpdateImageOnPageData): void {
		if (data.dynamicUiSubtype) {
			this.remixSubtype = data.dynamicUiSubtype;
		}
	}

	settleInlineRovoEditAttempt(attemptId: string): boolean {
		return this.reportInlineRovoEditAttempt(attemptId, 'generation-aborted');
	}

	/**
	 * Clears editor-side loading state for image updates. This is safe even before
	 * image prepare data arrives, which can happen when Rovo chat blocks a request.
	 */
	clearStreamingState(
		terminal: 'generation-aborted' | 'generation-error' = 'generation-aborted',
		terminalAttemptId?: string,
	): boolean {
		const attemptId = terminalAttemptId ?? this.inlineRovoEditAttemptId;
		const wasReported = this.reportInlineRovoEditAttempt(attemptId, terminal);
		// Tokenless callers retain the legacy cleanup path.
		if (terminalAttemptId && !wasReported) {
			return false;
		}

		this.streamRevision += 1;
		if (this.chatRemixEdit) {
			this.chatRemixEdit.settled = true;
		}
		const editorAPI = this.editorAPI as PassThroughCommandProps['editorAPI'] | undefined;
		editorAPI?.aiExperience?.actions.endInlineRovoStreaming?.();
		editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		this.hasPrepared = false;
		this.lastTraceId = undefined;
		this.remixSubtype = undefined;
		return true;
	}

	/**
	 * Called when the backend sends an SSE ACTION_ERROR or a prepared image stream is interrupted
	 * to reset the in-flight stream state and fail the remix experience.
	 */
	streamError(
		failureStage: 'backend_action' | 'stream_lifecycle' = 'stream_lifecycle',
		traceId?: string,
	): void {
		this.streamRevision += 1;

		const chatRemixEdit = this.chatRemixEdit;
		const chatRemixInstrumentationId = chatRemixEdit?.instrumentationId;
		const failureTraceId = traceId ?? this.lastTraceId;
		this.hasPrepared = false;
		this.reportInlineRovoEditAttempt(this.inlineRovoEditAttemptId, 'generation-error');
		this.lastTraceId = undefined;
		this.reportExperience(chatRemixEdit, () =>
			remixRegenerateExperience.fail({
				...(chatRemixInstrumentationId && { instrumentationId: chatRemixInstrumentationId }),
				error: 'stream_error',
				failureReason:
					failureStage === 'backend_action'
						? 'backend_action_error'
						: 'stream_interrupted_after_prepare',
				failureStage,
				remixType: 'infographic',
				...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
				...(failureTraceId && { traceId: failureTraceId }),
			}),
		);
		this.remixSubtype = undefined;
	}

	/**
	 * Called on abort when ACTION_PREPARE was received, matching the legacy image
	 * abort behavior.
	 */
	abortIfPrepared(): void {
		if (this.hasPrepared) {
			this.streamError('stream_lifecycle');
		}
	}

	abortAndClearStreamingState(reason?: AbortReason, options?: AbortOptions): void {
		const chatRemixEdit = this.chatRemixEdit;
		if (chatRemixEdit) {
			if (reason === 'user_abort') {
				this.reportExperience(chatRemixEdit, () =>
					remixRegenerateExperience.abort({
						instrumentationId: chatRemixEdit.instrumentationId,
						reason: 'User cancelled chat remix edit',
					}),
				);
			} else {
				this.reportExperience(chatRemixEdit, () =>
					remixRegenerateExperience.fail({
						instrumentationId: chatRemixEdit.instrumentationId,
						error: 'stream_error',
						failureReason: 'stream_interrupted_after_prepare',
						failureStage: 'stream_lifecycle',
					}),
				);
			}
		}
		if (
			this.hasPrepared &&
			!chatRemixEdit &&
			reason === 'user_abort' &&
			options?.isExplicitUserCancellation
		) {
			remixRegenerateExperience.abort({
				reason: 'User cancelled infographic edit',
				remixType: 'infographic',
				...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
				streamAbortReason: 'user_abort',
				...(this.lastTraceId && { traceId: this.lastTraceId }),
			});
		} else if (this.hasPrepared && !chatRemixEdit) {
			const streamAbortReason = toStreamAbortReason(reason);
			this.reportExperience(chatRemixEdit, () =>
				remixRegenerateExperience.fail({
					error: 'stream_error',
					failureReason: 'stream_interrupted_after_prepare',
					failureStage: 'stream_lifecycle',
					remixType: 'infographic',
					...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
					...(streamAbortReason && { streamAbortReason }),
					...(this.lastTraceId && { traceId: this.lastTraceId }),
				}),
			);
		}
		this.clearStreamingState();
	}

	private claimInlineRovoEditAttempt(data: ConfluenceUpdateImageOnPageData): void {
		const targetId = data.dynamicUiSource?.startLocalId;
		if (this.hasForwardedInlineRovoEditClaim && this.lastInlineRovoEditClaimTarget === targetId) {
			return;
		}
		this.hasForwardedInlineRovoEditClaim = true;
		this.lastInlineRovoEditClaimTarget = targetId;
		const attemptId = this.editorAPI?.malleableUi?.actions.claimInlineRovoEdit?.(targetId);
		if (attemptId && !this.inlineRovoEditAttemptId) {
			this.inlineRovoEditAttemptId = attemptId;
		}
	}

	private reportInlineRovoEditAttempt(
		attemptId: string | undefined,
		result:
			| 'generation-aborted'
			| 'generation-error'
			| 'direct-insertion-started'
			| 'handoff'
			| 'insertion-failed'
			| 'inserted'
			| 'result-ready',
	): boolean {
		if (!attemptId) {
			if (
				result === 'generation-aborted' ||
				result === 'generation-error' ||
				result === 'handoff' ||
				result === 'insertion-failed' ||
				result === 'inserted'
			) {
				this.hasForwardedInlineRovoEditClaim = false;
				this.lastInlineRovoEditClaimTarget = undefined;
			}
			return false;
		}
		const wasReported =
			this.editorAPI?.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, result) ?? false;
		if (
			result === 'generation-aborted' ||
			result === 'generation-error' ||
			result === 'handoff' ||
			result === 'insertion-failed' ||
			result === 'inserted'
		) {
			if (this.inlineRovoEditAttemptId === attemptId) {
				this.inlineRovoEditAttemptId = undefined;
			}
			this.hasForwardedInlineRovoEditClaim = false;
			this.lastInlineRovoEditClaimTarget = undefined;
		}
		return wasReported;
	}
}
